import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, Route, Trash2 } from "lucide-react";
import { getStoreId } from "../utils/auth";
import * as logger from "../utils/logger";
import { showQuotaExceededAlert } from "../utils/quotaAlert";
import { shouldShowUpgradeToAuditAllProducts } from "../utils/auditCoverageCta";
import { showToast } from "../utils/toast";
import {
  normalizeSeoPage,
  pageMatchesOpportunityFilter,
  pickFirstAiFixTarget,
} from "../utils/seoOptimizerRecommendations";
import SEOOptimizerDetail from "./SEOOptimizerDetail";
import SEOOptimizerAutomation from "./SEOOptimizerAutomation";
import SEOOptimizerOverview from "./SEOOptimizerOverview";
import KeywordTracker from "./KeywordTracker";
import "./SEOOptimizer.css";

const SEOOptimizer = ({ initialTab, sidebarNavNonce = 0, onOpenPricing }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  const [loading, setLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    pagesAudited: { current: 0, total: 0 },
    products: { current: 0, total: 0 },
  });
  const [overallScore, setOverallScore] = useState(0);
  const [totalErrorsFromAPI, setTotalErrorsFromAPI] = useState(0);

  const [pages, setPages] = useState([]);
  const [filterType, setFilterType] = useState("Products");
  const [productListingFilter, setProductListingFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("Name - A to Z");
  const [scoreFilter, setScoreFilter] = useState("Any");
  const [issueFilter, setIssueFilter] = useState("Any");
  const [opportunityFilter, setOpportunityFilter] = useState(null);
  const [selectedItems, setSelectedItems] = useState(() => new Set());
  const [overviewInfoOpen, setOverviewInfoOpen] = useState(false);

  const [selectedPage, setSelectedPage] = useState(null);

  const [cacheMetadata, setCacheMetadata] = useState({
    isCached: false,
    validCount: 0,
    totalProducts: 0,
    outdatedCount: 0,
    unvalidatedCount: 0,
  });

  const [isValidating, setIsValidating] = useState(false);
  const [isRunningAudit, setIsRunningAudit] = useState(false);
  const [isCleaningOrphans, setIsCleaningOrphans] = useState(false);
  const [quotaStatus, setQuotaStatus] = useState(null);

  const fetchInFlightRef = useRef(false);
  const automationRef = useRef(null);

  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();

  const hasCachedData =
    cacheMetadata.isCached &&
    ((cacheMetadata.validCount || 0) > 0 || (cacheMetadata.totalProducts || 0) > 0);

  const normalizedPages = useMemo(
    () => pages.map((p) => normalizeSeoPage(p)),
    [pages]
  );

  useEffect(() => {
    const tab = initialTab || "overview";
    setActiveTab(tab);
    if (tab !== "overview") {
      setSelectedPage(null);
    }
  }, [initialTab, sidebarNavNonce]);

  useEffect(() => {
    if (filterType !== "Products") {
      setProductListingFilter("all");
    }
  }, [filterType]);

  useEffect(() => {
    const current = (window.location.hash || "").replace("#", "");
    if (activeTab === "keywordTracker") {
      if (current !== "keyword-tracker") window.location.hash = "keyword-tracker";
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "overview") {
      setOverviewInfoOpen(false);
    }
  }, [activeTab]);

  const fetchPages = async (cachedOverride) => {
    const canFetch = cachedOverride ?? hasCachedData;
    if (!hashSiteId || !canFetch) {
      setPages([]);
      return;
    }
    const pagesUrl = new URL(`${apiUrl}/api/seo-optimizer/pages`);
    pagesUrl.searchParams.set("id", hashSiteId);
    pagesUrl.searchParams.set("type", filterType);
    pagesUrl.searchParams.set("limit", "10000");
    pagesUrl.searchParams.set("useCache", "true");
    if (searchQuery) {
      pagesUrl.searchParams.set("search", searchQuery);
    }
    if (filterType === "Products" && productListingFilter === "online") {
      pagesUrl.searchParams.set("onlineOnly", "true");
    }
    const pagesRes = await fetch(pagesUrl.toString());
    if (pagesRes.ok) {
      const pagesData = await pagesRes.json();
      setPages(pagesData.pages || []);
    }
  };

  const refetchData = async () => {
    if (!hashSiteId) return;

    setLoading(true);
    setError(null);

    try {
      const [statsRes, overviewRes] = await Promise.all([
        fetch(`${apiUrl}/api/seo-optimizer/stats?id=${hashSiteId}`).catch(() => null),
        fetch(`${apiUrl}/api/seo-cache/overview-stats?id=${hashSiteId}`).catch(() => null),
      ]);

      if (statsRes?.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      let cached = false;
      if (overviewRes?.ok) {
        const overviewData = await overviewRes.json();
        setOverallScore(overviewData.overallScore || 0);
        setTotalErrorsFromAPI(overviewData.totalErrors || 0);
        if (overviewData.cacheMetadata) {
          setCacheMetadata(overviewData.cacheMetadata);
          cached =
            overviewData.cacheMetadata.isCached &&
            ((overviewData.cacheMetadata.validCount || 0) > 0 ||
              (overviewData.cacheMetadata.totalProducts || 0) > 0);
        }
        if (overviewData.cacheMetadata?.quota_usage) {
          setQuotaStatus(overviewData.cacheMetadata.quota_usage);
        }
      }

      if (cached) {
        await fetchPages(true);
      } else {
        setPages([]);
      }
    } catch (err) {
      logger.error("Error fetching SEO data:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setIsLoadingStats(true);
    refetchData().finally(() => {
      fetchInFlightRef.current = false;
      setIsLoadingStats(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashSiteId]);

  useEffect(() => {
    if (!hashSiteId || !hasCachedData) return;
    void fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashSiteId, filterType, productListingFilter, searchQuery, hasCachedData]);

  const sortedPages = useMemo(() => {
    if (!normalizedPages.length) return [];
    const sorted = [...normalizedPages];
    const safeName = (val) => (val == null ? "" : String(val));
    switch (sortBy) {
      case "Name - A to Z":
        return sorted.sort((a, b) => safeName(a.name).localeCompare(safeName(b.name)));
      case "Name - Z to A":
        return sorted.sort((a, b) => safeName(b.name).localeCompare(safeName(a.name)));
      case "SEO Score - High to Low":
        return sorted.sort((a, b) => (b.seoScore || 0) - (a.seoScore || 0));
      case "SEO Score - Low to High":
        return sorted.sort((a, b) => (a.seoScore || 0) - (b.seoScore || 0));
      default:
        return sorted;
    }
  }, [normalizedPages, sortBy]);

  const filteredPages = useMemo(() => {
    return sortedPages.filter((page) => {
      const pageName = page?.name ? String(page.name) : "";
      const nameMatch =
        !searchQuery || pageName.toLowerCase().includes(searchQuery.toLowerCase());

      const scoreMatch = (() => {
        if (scoreFilter === "Any") return true;
        const score = page.seoScore || 0;
        if (scoreFilter === "96-100") return score > 95;
        if (scoreFilter === "80-95") return score >= 80 && score <= 95;
        if (scoreFilter === "0-79") return score < 80;
        return true;
      })();

      const issuesMatch = (() => {
        if (issueFilter === "Any") return true;
        const totalIssues =
          (page.meta || 0) + (page.content || 0) + (page.image || 0) + (page.broken || 0) + (page.url || 0);
        if (issueFilter === "Critical") {
          return (page.meta || 0) > 0 || (page.broken || 0) > 0 || (page.url || 0) > 0;
        }
        if (issueFilter === "None") return totalIssues === 0;
        return true;
      })();

      return nameMatch && scoreMatch && issuesMatch && pageMatchesOpportunityFilter(page, opportunityFilter);
    });
  }, [sortedPages, searchQuery, scoreFilter, issueFilter, opportunityFilter]);

  const totalPages = Math.ceil(filteredPages.length / itemsPerPage) || 1;

  const paginatedPages = useMemo(() => {
    return filteredPages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredPages, currentPage, itemsPerPage]);

  const summaryStats = useMemo(() => {
    const totalErrors = totalErrorsFromAPI;
    const avgScore =
      overallScore > 0
        ? overallScore
        : normalizedPages.length
          ? Math.round(
              normalizedPages.reduce((sum, page) => sum + (page.seoScore || 0), 0) /
                normalizedPages.length
            )
          : 0;
    return { totalErrors, avgScore };
  }, [normalizedPages, totalErrorsFromAPI, overallScore]);

  const areFiltersActive = useMemo(() => {
    return (
      filterType !== "Products" ||
      productListingFilter !== "all" ||
      scoreFilter !== "Any" ||
      issueFilter !== "Any" ||
      opportunityFilter != null ||
      (searchQuery && searchQuery.trim() !== "")
    );
  }, [filterType, productListingFilter, scoreFilter, issueFilter, searchQuery, opportunityFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems(new Set());
  }, [filterType, productListingFilter, searchQuery, opportunityFilter, scoreFilter, issueFilter]);

  const handleRunAudit = async () => {
    if (!hashSiteId) return;

    setIsRunningAudit(true);
    try {
      let requestBody = { scope: "all" };
      if (areFiltersActive && filteredPages.length > 0) {
        requestBody = {
          scope: "specific",
          pageIds: filteredPages.map((page) => page.id.toString()),
        };
      }

      const response = await fetch(`${apiUrl}/api/seo-optimizer/validate?id=${hashSiteId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const errorData = await response.json().catch(() => ({}));
      if (response.status === 409 && (errorData.code === "VALIDATION_IN_PROGRESS" || errorData.error)) {
        showToast(errorData.error || t("seoOptimizer.validationInProgress"), "info", 6000);
      } else if (response.status === 403 && (errorData.error === "Quota exceeded" || errorData.data)) {
        const q = errorData.data || {};
        const message = t("dashboard.quotaExceededToast", {
          used: q.used ?? "?",
          limit: q.limit ?? "?",
          plan: q.accountType || "trial",
        });
        await showQuotaExceededAlert(t("dashboard.quotaExceededTitle"), message, t("common.ok"));
      } else if (response.ok) {
        showToast(t("seoOptimizer.validationStarted"), "success");
        setTimeout(() => {
          void refetchData();
        }, 2000);
      } else {
        showToast(errorData.error || t("seoOptimizer.failedToStartValidation"), "error");
      }
    } catch (err) {
      logger.error("Error running audit:", err);
      showToast(err.message || t("seoOptimizer.failedToStartValidation"), "error");
    } finally {
      setIsRunningAudit(false);
    }
  };

  const handleValidateOutdated = async () => {
    if (!hashSiteId) return;
    try {
      setIsValidating(true);
      const response = await fetch(`${apiUrl}/api/seo-optimizer/validate?id=${hashSiteId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope: "outdated" }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 409 && (data.code === "VALIDATION_IN_PROGRESS" || data.error)) {
        showToast(data.error || t("seoOptimizer.validationInProgress"), "info", 6000);
      } else if (response.ok) {
        showToast(t("seoOptimizer.validationStarted"), "success");
        setTimeout(() => {
          void refetchData();
        }, 2000);
      } else {
        showToast(data.error || t("seoOptimizer.failedToStartValidation"), "error");
      }
    } catch (validationError) {
      logger.error("Validation error:", validationError);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!hashSiteId) return;
    try {
      setIsCleaningOrphans(true);
      const response = await fetch(`${apiUrl}/api/seo-optimizer/cleanup-orphans?id=${hashSiteId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showToast(data.error || "Failed to clean orphaned pages.", "error");
        return;
      }
      const totalRemoved = Number(data.totalRemoved || 0);
      const productsRemoved = Number(data.productsRemoved || 0);
      const categoriesRemoved = Number(data.categoriesRemoved || 0);
      showToast(
        totalRemoved > 0
          ? `Cleaned ${totalRemoved} orphaned row(s): ${productsRemoved} products, ${categoriesRemoved} categories.`
          : "No orphaned products or categories were found.",
        "success",
        5000
      );
      await refetchData();
    } catch (err) {
      logger.error("Cleanup orphans error:", err);
      showToast(err.message || "Failed to clean orphaned pages.", "error");
    } finally {
      setIsCleaningOrphans(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === paginatedPages.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedPages.map((p) => p.id)));
    }
  };

  const handleSelectItem = (id) => {
    const next = new Set(selectedItems);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedItems(next);
  };

  const handleOptimizeSelected = () => {
    if (selectedItems.size === 0) return;
    const selected = normalizedPages.filter((p) => selectedItems.has(p.id));
    const target = pickFirstAiFixTarget(selected);
    if (target) {
      setSelectedPage(target);
    }
  };

  const handlePageClick = (page) => {
    setSelectedPage(page);
  };

  const handleBackToList = () => {
    setSelectedPage(null);
    void refetchData();
  };

  if (selectedPage) {
    const selectedIndex = filteredPages.findIndex((p) => p.id === selectedPage.id);
    return (
      <SEOOptimizerDetail
        page={selectedPage}
        pages={filteredPages}
        currentIndex={selectedIndex >= 0 ? selectedIndex : 0}
        onSelectPage={(page) => setSelectedPage(page)}
        onClose={handleBackToList}
        onSave={() => {
          setSelectedPage(null);
          void refetchData();
        }}
      />
    );
  }

  const pageTitle =
    activeTab === "automation"
      ? t("mainApp.nav.seoBulkOptimizer")
      : activeTab === "keywordTracker"
        ? t("mainApp.nav.keywordTracker")
        : t("mainApp.nav.aiSeoOptimizer");

  const showUpgradeCta = shouldShowUpgradeToAuditAllProducts(quotaStatus, cacheMetadata);

  return (
    <div className="container-fluid py-1">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 fw-bold">{pageTitle}</h4>
        <div className="d-flex gap-2 align-items-center">
          {activeTab === "automation" && (
            <button
              id="bulk-page-tour-button"
              type="button"
              className="btn btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => automationRef.current?.beginBulkOptimizeTour?.()}
              title={t("bulkOptimizeTour.welcomeTitle")}
            >
              <Route size={16} aria-hidden />
              {t("bulkOptimizeTour.pageTourButton")}
            </button>
          )}
          {activeTab === "overview" && (
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-1"
              onClick={handleCleanupOrphans}
              disabled={isCleaningOrphans}
              title="Remove deleted products and categories from the control panel"
            >
              <Trash2 size={16} aria-hidden />
              {isCleaningOrphans ? "Cleaning..." : "Clean deleted pages"}
            </button>
          )}
          {activeTab !== "overview" && (
            <button
              className="btn btn-primary"
              onClick={handleRunAudit}
              disabled={isValidating || isRunningAudit}
            >
              {isValidating || isRunningAudit ? (
                <>
                  <RefreshCw size={16} className="me-2 spinning" />
                  {t("seoOptimizer.running")}
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="me-2" />
                  {t("seoOptimizer.runAudit")}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {activeTab === "overview" && overviewInfoOpen && (
        <div
          className="card border mb-3 border-start border-2 rounded-end"
          style={{ backgroundColor: "#f8f9fa", borderLeftColor: "#adb5bd" }}
        >
          <div className="card-body py-2 px-3">
            <ul className="mb-0 ps-3" style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
              <li className="fw-semibold mb-1">
                <span className="text-dark">{t("seoOptimizer.infoCardOverview1Label")}</span>{" "}
                {t("seoOptimizer.infoCardOverview1Desc")}
              </li>
              <li className="fw-semibold mb-1">
                <span className="text-dark">{t("seoOptimizer.infoCardOverview2Label")}</span>{" "}
                {t("seoOptimizer.infoCardOverview2Desc")}
              </li>
              <li className="fw-semibold">
                <span className="text-dark">{t("seoOptimizer.infoCardOverview3Label")}</span>{" "}
                {t("seoOptimizer.infoCardOverview3Desc")}
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === "overview" && (
        <SEOOptimizerOverview
          pages={normalizedPages}
          paginatedPages={paginatedPages}
          filteredPages={filteredPages}
          totalErrors={summaryStats.totalErrors}
          avgScore={summaryStats.avgScore}
          stats={{
            pagesAudited: stats.pagesAudited,
            products: stats.products || { current: 0, total: 0 },
          }}
          cacheMetadata={cacheMetadata}
          hasCachedData={hasCachedData}
          isLoading={loading}
          isLoadingStats={isLoadingStats}
          isRunningAudit={isRunningAudit}
          isValidating={isValidating}
          error={error}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          onFixWithAi={handlePageClick}
          onOptimizeSelected={handleOptimizeSelected}
          onOpportunityFix={(bucketId) => {
            setOpportunityFilter(bucketId);
            setCurrentPage(1);
          }}
          onRunAudit={handleRunAudit}
          onValidateOutdated={handleValidateOutdated}
          onRefresh={() => refetchData()}
          onUpgradeToAuditAllProducts={
            onOpenPricing && showUpgradeCta ? onOpenPricing : undefined
          }
          onAuditAllProducts={!showUpgradeCta ? handleRunAudit : undefined}
          isAuditingAllProducts={isRunningAudit}
          opportunityFilter={opportunityFilter}
          onClearOpportunityFilter={() => setOpportunityFilter(null)}
          filterType={filterType}
          setFilterType={setFilterType}
          productListingFilter={productListingFilter}
          setProductListingFilter={setProductListingFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          scoreFilter={scoreFilter}
          setScoreFilter={setScoreFilter}
          issueFilter={issueFilter}
          setIssueFilter={setIssueFilter}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalPages={totalPages}
          areFiltersActive={areFiltersActive}
        />
      )}

      {activeTab === "keywordTracker" && <KeywordTracker />}

      {activeTab === "automation" && (
        <>
          <div
            className="card border mb-3 border-start border-2 rounded-end"
            style={{ backgroundColor: "#f8f9fa", borderLeftColor: "#adb5bd" }}
          >
            <div className="card-body py-2 px-3">
              <ul className="mb-0 ps-3" style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
                <li className="fw-semibold mb-1">
                  <span className="text-dark">{t("seoOptimizer.infoCardAutomation1Label")}</span>
                  {t("seoOptimizer.infoCardAutomation1Desc")
                    ? ` ${t("seoOptimizer.infoCardAutomation1Desc")}`
                    : ""}
                </li>
                <li className="fw-semibold mb-1">
                  <span className="text-dark">{t("seoOptimizer.infoCardAutomation2Label")}</span>{" "}
                  {t("seoOptimizer.infoCardAutomation2Desc")}
                </li>
                <li className="fw-semibold">
                  <span className="text-dark">{t("seoOptimizer.infoCardAutomation3Label")}</span>
                  {t("seoOptimizer.infoCardAutomation3Desc")
                    ? ` ${t("seoOptimizer.infoCardAutomation3Desc")}`
                    : ""}
                </li>
              </ul>
            </div>
          </div>
          <SEOOptimizerAutomation ref={automationRef} />
        </>
      )}

      {activeTab === "settings" && (
        <div className="card">
          <div className="card-body">
            <h5>{t("seoOptimizer.settings")}</h5>
            <p className="text-muted">{t("seoOptimizer.comingSoon")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOOptimizer;
